// BoardMembersTab.tsx — Sprint 15. Card with the board-member list +
// Add button. List item includes avatar (greyed when inactive),
// role badge, contact details, and term range.
import React from 'react';
import { Avatar, Button, Card, List, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined, EditOutlined, MailOutlined, PhoneOutlined, PlusOutlined,
  TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { BoardMember } from '../../types';

const { Text } = Typography;

export interface BoardMembersTabProps {
  members: BoardMember[];
  onAdd:     () => void;
  onEdit:    (member: BoardMember) => void;
  onDelete:  (id: string) => void;
}

export const BoardMembersTab: React.FC<BoardMembersTabProps> = ({
  members, onAdd, onEdit, onDelete,
}) => (
  <Card
    title="Current Board Members"
    extra={<Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>Add Member</Button>}
  >
    <List
      dataSource={members}
      locale={{
        emptyText: (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16, display: 'block' }} />
            <div style={{ color: '#999', marginBottom: 16 }}>No board members yet.</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>Add Member</Button>
          </div>
        ),
      }}
      renderItem={(m: BoardMember) => (
        <List.Item
          key={m.id}
          actions={[
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(m)} />
            </Tooltip>,
            <Popconfirm title="Remove this board member?" onConfirm={() => onDelete(m.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>,
          ]}
        >
          <List.Item.Meta
            avatar={
              <Avatar
                icon={<UserOutlined />}
                style={{ backgroundColor: m.isActive ? '#2f54eb' : '#d9d9d9' }}
              />
            }
            title={
              <Space>
                <Text strong>{m.name}</Text>
                <Tag color="blue">{m.role}</Tag>
                {!m.isActive && <Tag color="default">Inactive</Tag>}
              </Space>
            }
            description={
              <Space size="large">
                <Text type="secondary"><UserOutlined style={{ marginRight: 4 }} />Unit {m.unitNumber}</Text>
                <Text type="secondary"><MailOutlined style={{ marginRight: 4 }} />{m.email}</Text>
                {m.phone && (
                  <Text type="secondary"><PhoneOutlined style={{ marginRight: 4 }} />{m.phone}</Text>
                )}
                <Text type="secondary">
                  Term: {dayjs(m.termStart).format('MMM YYYY')} – {dayjs(m.termEnd).format('MMM YYYY')}
                </Text>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  </Card>
);
